import { spawn } from 'node:child_process'
import { stripVTControlCharacters } from 'node:util'

import type { RunMode, RunVercelResult, VercelRunner } from './types.js'

/**
 * Vercel CLI version used for the `npx` fallback. Intentionally tracks `latest`
 * rather than a hard pin: `vercel@48` mishandles `integration add neon` on
 * accounts that already have the Neon integration installed — it tries to
 * re-install the integration and fails with "Cannot install more than one
 * integration at a time", instead of provisioning a new resource under the
 * existing installation. v54+ handles this correctly. If a future release
 * changes `integration add` flags or `--format json` output that `neon.ts`
 * depends on, re-pin to a known-good version here.
 */
export const VERCEL_CLI_VERSION = 'latest'

const DEFAULT_CAPTURE_TIMEOUT_MS = 120_000

function pathRunner(): VercelRunner {
  return { bin: 'vercel', prefix: [], source: 'path' }
}

function npxRunner(): VercelRunner {
  return { bin: 'npx', prefix: ['-y', `vercel@${VERCEL_CLI_VERSION}`], source: 'npx' }
}

/**
 * Detect a usable Vercel runner. Prefers a global `vercel` on PATH (no download,
 * respects the user's installed version); falls back to `npx vercel@<pin>`.
 * npx reachability is probed lazily at first real use so offline degrades to a
 * soft failure rather than blocking here.
 */
export async function resolveVercelRunner(): Promise<VercelRunner> {
  const probe = await runVercel(pathRunner(), ['--version'], {
    cwd: process.cwd(),
    mode: 'capture',
    timeoutMs: 10_000
  })
  return probe.success ? pathRunner() : npxRunner()
}

/**
 * Buffer stream chunks into lines and forward the ones `shouldShow` accepts.
 * Lines are matched on their ANSI-stripped, trimmed content but written
 * verbatim; `\r` counts as a line break so carriage-return spinner redraws
 * cannot hold real lines hostage in the buffer.
 */
export function createFilteredLineForwarder(
  shouldShow: (line: string) => boolean,
  write: (text: string) => void
): { push(chunk: string): void; flush(): void } {
  let pending = ''

  const emit = (line: string) => {
    if (shouldShow(stripVTControlCharacters(line).trim())) write(`${line}\n`)
  }

  return {
    push(chunk: string) {
      pending += chunk
      const lines = pending.split(/\r\n|\n|\r/)
      pending = lines.pop() ?? ''
      for (const line of lines) emit(line)
    },
    flush() {
      if (pending !== '') emit(pending)
      pending = ''
    }
  }
}

/**
 * Core subprocess primitive for the Vercel CLI. Modeled on `runQuietCommand`
 * (capture) and `runDrizzlePush` (inherit) in the init command.
 * - `capture`: piped stdio with a timeout; collects stdout/stderr for parsing.
 * - `inherit`: interactive stdio for marketplace terms so the user can click
 *   through Vercel's own prompts. No output is captured.
 * - `stream`: stdin inherited, stdout/stderr piped through `streamLineFilter`
 *   (return false to suppress a line); everything is still captured for
 *   debugging. Used for `vercel login` so the device link stays visible while
 *   banner/upsell noise is dropped.
 * `env` is merged over `process.env`, which is how `VERCEL_TOKEN` flows through.
 * `stdinInput` (capture mode only) is written to the child's stdin and the
 * stream is closed — how `vercel env add` receives its value without a TTY.
 * `onOutputLine` observes each non-empty stdout/stderr line without changing
 * capture behavior, including carriage-return progress redraws. Its second
 * argument lets the observer re-arm the timeout (counted from now) when a line
 * reveals the command is waiting on a human — e.g. browser terms acceptance.
 */
export function runVercel(
  runner: VercelRunner,
  args: string[],
  options: {
    cwd: string
    mode: RunMode
    timeoutMs?: number
    env?: NodeJS.ProcessEnv
    streamLineFilter?: (line: string) => boolean
    stdinInput?: string
    onOutputLine?: (line: string, run: { extendTimeout: (ms: number) => void }) => void
  }
): Promise<RunVercelResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CAPTURE_TIMEOUT_MS
  const argv = [...runner.prefix, ...args]

  return new Promise((resolve) => {
    const child = spawn(runner.bin, argv, {
      cwd: options.cwd,
      stdio:
        options.mode === 'inherit'
          ? 'inherit'
          : options.mode === 'stream'
            ? ['inherit', 'pipe', 'pipe']
            : 'pipe',
      // npm_config_loglevel silences npx's "npm warn deprecated" install noise
      // on the npx runner; harmless for a PATH-installed vercel.
      env: { ...process.env, npm_config_loglevel: 'error', ...options.env }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false
    let closed = false
    let effectiveTimeoutMs = timeoutMs
    let timeout: NodeJS.Timeout | undefined
    const armTimeout = (ms: number) => {
      if (closed) return
      clearTimeout(timeout)
      effectiveTimeoutMs = ms
      timeout = setTimeout(() => {
        timedOut = true
        child.kill('SIGTERM')
      }, ms)
    }
    const runControl = { extendTimeout: armTimeout }
    const onOutputLine = options.onOutputLine
    const createOutputObserver = () => {
      if (!onOutputLine) return undefined
      return createFilteredLineForwarder(
        (line) => line.length > 0,
        (text) => onOutputLine(stripVTControlCharacters(text).trim(), runControl)
      )
    }
    const stdoutObserver = createOutputObserver()
    const stderrObserver = createOutputObserver()

    if (options.mode === 'capture' && options.stdinInput !== undefined) {
      child.stdin?.write(options.stdinInput)
      child.stdin?.end()
    }

    if (options.mode === 'capture') {
      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        stdoutObserver?.push(text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        stderrObserver?.push(text)
      })
    } else if (options.mode === 'stream') {
      const shouldShow = options.streamLineFilter ?? (() => true)
      const outForwarder = createFilteredLineForwarder(shouldShow, (text) =>
        process.stdout.write(text)
      )
      const errForwarder = createFilteredLineForwarder(shouldShow, (text) =>
        process.stderr.write(text)
      )
      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        outForwarder.push(text)
        stdoutObserver?.push(text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        errForwarder.push(text)
        stderrObserver?.push(text)
      })
      child.on('close', () => {
        outForwarder.flush()
        errForwarder.flush()
      })
    }

    armTimeout(timeoutMs)

    child.on('close', (code, signal) => {
      closed = true
      clearTimeout(timeout)
      stdoutObserver?.flush()
      stderrObserver?.flush()

      if (timedOut) {
        resolve({
          success: false,
          code,
          signal,
          stdout,
          stderr,
          timedOut: true,
          errorMessage: `Vercel command timed out after ${Math.floor(effectiveTimeoutMs / 1000)}s`
        })
        return
      }

      resolve({
        success: code === 0,
        code,
        signal,
        stdout,
        stderr,
        timedOut: false,
        errorMessage:
          code === 0
            ? undefined
            : signal
              ? `Vercel command terminated by ${signal}`
              : `Vercel command exited with code ${code ?? 'unknown'}`
      })
    })

    child.on('error', (err) => {
      closed = true
      clearTimeout(timeout)
      stdoutObserver?.flush()
      stderrObserver?.flush()
      resolve({
        success: false,
        code: null,
        signal: null,
        stdout,
        stderr,
        timedOut: false,
        errorMessage: err.message
      })
    })
  })
}

/**
 * Parse a JSON payload out of mixed Vercel stdout. The CLI can print human
 * banners around `--format json` output, so we try the whole string first, then
 * fall back to the outermost `{...}`/`[...]` span. Any parse miss returns
 * undefined (soft failure) rather than throwing.
 */
export function parseVercelJson<T = unknown>(stdout: string): T | undefined {
  const trimmed = stdout.trim()
  if (!trimmed) return undefined

  try {
    return JSON.parse(trimmed) as T
  } catch {
    // fall through to span extraction
  }

  const span = extractOutermostJson(trimmed)
  if (!span) return undefined

  try {
    return JSON.parse(span) as T
  } catch {
    return undefined
  }
}

/** Slice the span from the first `{`/`[` to the last matching `}`/`]`. */
function extractOutermostJson(text: string): string | undefined {
  const starts = [text.indexOf('{'), text.indexOf('[')].filter((i) => i >= 0)
  if (starts.length === 0) return undefined
  const start = Math.min(...starts)

  const end = Math.max(text.lastIndexOf('}'), text.lastIndexOf(']'))
  if (end <= start) return undefined

  return text.slice(start, end + 1)
}
